import { useMutation } from '@apollo/react-hooks';
import gql from 'graphql-tag';
import React, { useState } from 'react';
import { Platform, StyleSheet, View } from 'react-native';
import { Button, Dialog, Portal, TextInput } from 'react-native-paper';
import useConfirmModal from 'shared/hooks/useConfirmModal';
import useOrientation from 'shared/hooks/useOrientation';
import useSnackBar from 'shared/hooks/useSnackBar';
import { sizes } from 'shared/styles';

interface Props {
  trackId: string;
  onDismiss: () => void;
  onSuccess: () => void;
}

const UPDATE_PRICE_MUTATION = gql`
  mutation UpdatePrice($input: UpdatePriceInput!) {
    trackUpdatePrice(input: $input) {
      id
      name
      prices {
        id
        name
        token
        displayText
      }
    }
  }
`;

const UpdatePriceModal = ({ trackId, onDismiss, onSuccess }: Props) => {
  const confirmWith = useConfirmModal();
  const showSnackBar = useSnackBar();
  const { vh } = useOrientation();
  const [stripePriceId, setStripePriceId] = useState('');

  const [updatePrice, { loading }] = useMutation(UPDATE_PRICE_MUTATION);

  const displayError = (message: string) => {
    confirmWith({ title: 'Error', message, hideCancel: true });
  };

  const onSave = () => {
    if (!stripePriceId.trim()) {
      displayError('Please enter a Stripe Price ID');
      return;
    }

    updatePrice({
      variables: {
        input: {
          trackId,
          stripePriceId: stripePriceId.trim(),
        },
      },
    })
      .then(({ data }) => {
        onSuccess();
        showSnackBar({
          message: `Successfully updated price for: ${data.trackUpdatePrice.name}`,
        });
      })
      .catch(({ graphQLErrors }) => {
        const message =
          graphQLErrors && graphQLErrors.map((e: any) => e.message).join(', ');
        displayError(message || 'Error updating price. Please, try again!');
      });
  };

  return (
    <Portal>
      <Dialog
        visible
        onDismiss={onDismiss}
        style={[{ maxHeight: vh(60) }, styles.dialog]}
      >
        <Dialog.Title>Update Price</Dialog.Title>
        <View
          style={{
            marginHorizontal: 20,
            marginBottom: sizes.spacing,
            justifyContent: 'center',
          }}
        >
          <TextInput
            label="Stripe Price ID"
            mode="outlined"
            value={stripePriceId}
            onChangeText={setStripePriceId}
            style={{ marginBottom: 20 }}
            placeholder="Enter Stripe Price ID"
          />
        </View>

        <Dialog.Actions style={styles.actionsContainer}>
          <Button mode="outlined" onPress={onDismiss}>
            Cancel
          </Button>
          <Button
            loading={loading}
            disabled={loading || !stripePriceId.trim()}
            mode="contained"
            icon="cash"
            onPress={onSave}
          >
            Update Price
          </Button>
        </Dialog.Actions>
      </Dialog>
    </Portal>
  );
};

const styles = StyleSheet.create({
  dialog: Platform.select({
    default: {},
    web: {
      alignSelf: 'center',
      width: 500,
    },
  }),
  actionsContainer: {
    paddingVertical: sizes.spacing,
    flexDirection: 'row',
    justifyContent: 'space-around',
    alignItems: 'center',
  },
});

export default UpdatePriceModal;
